import { keyframes } from '@mui/material/styles';
import type { CSSObject, SxProps, Theme } from '@mui/material/styles';

export const TEMPLE = {
  saffron: '#E8721C',
  saffronD: '#C85A10',
  saffronL: '#F0A030',
  gold: '#C89B3C',
  goldD: '#A87E28',
  goldL: '#E8D5A8',
  maroon: '#7A1E1E',
  maroonD: '#5E1414',
  deep: '#8A2B10',
  cream: '#FDF8EE',
  cream2: '#FAF2E2',
  cream3: '#F5EAD3',
  ink: '#3A2A1A',
  inkM: '#6B5843',
  inkL: '#9C8A72',
  line: '#E8D9BC',
  lineD: '#D8C49A',
} as const;

export const TEMPLE_FONTS = {
  display: '"Playfair Display", serif',
  body: '"Mukta", sans-serif',
  dev: '"Yatra One", cursive',
} as const;

export const TOPBAR_H = { xs: 66, md: 78 } as const;
export const SIDENAV_W = 275;

/* ---------------------------- animations ---------------------------- */

export const navStripeFlow = keyframes`
  to { background-position: 220% 0; }
`;

export const mandalaSpin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const reduceMotion = '@media (prefers-reduced-motion: reduce)';

/* ------------------------------ surfaces ---------------------------- */

export const templeAppBarSx: CSSObject = {
  backgroundColor: 'rgba(253, 248, 238, 0.92)',
  backdropFilter: 'blur(18px)',
  borderBottom: `1px solid ${TEMPLE.line}`,
  boxShadow: '0 2px 24px rgba(138, 43, 16, 0.07)',
  color: TEMPLE.ink,
};

export const templeStripeSx: SxProps<Theme> = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '-1px',
  height: '3px',
  background: `linear-gradient(90deg, ${TEMPLE.saffron}, ${TEMPLE.gold}, ${TEMPLE.maroon}, ${TEMPLE.gold}, ${TEMPLE.saffron})`,
  backgroundSize: '220% 100%',
  animation: `${navStripeFlow} 9s linear infinite`,
  [reduceMotion]: { animation: 'none' },
};

export const templeDrawerPaperSx: CSSObject = {
  backgroundColor: TEMPLE.cream,
  backgroundImage: `linear-gradient(180deg, ${TEMPLE.cream} 0%, ${TEMPLE.cream2} 50%)`,
  borderRight: `1px solid ${TEMPLE.line}`,
  boxShadow: 'none',
};

export const templePillGroupSx: SxProps<Theme> = {
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '999px',
  border: `1.5px solid ${TEMPLE.gold}`,
  backgroundColor: '#fff',
};

export const templePillSx = (active: boolean): SxProps<Theme> => ({
  minWidth: 0,
  px: 1.5,
  py: '5px',
  borderRadius: 0,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none',
  color: active ? '#fff' : TEMPLE.inkM,
  background: active
    ? `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`
    : 'transparent',
  '&:hover': {
    background: active
      ? `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`
      : TEMPLE.cream2,
  },
});

export const templeIconButtonSx: CSSObject = {
  color: TEMPLE.maroon,
  '&:hover': { backgroundColor: TEMPLE.cream2 },
};

export const templeSeparatorSx: SxProps<Theme> = {
  width: '1px',
  height: 26,
  mx: 1,
  backgroundColor: TEMPLE.line,
};

export const templeSectionLabelSx: SxProps<Theme> = {
  px: 2,
  pt: 2,
  pb: 0.75,
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: '3px',
  textTransform: 'uppercase',
  color: TEMPLE.goldD,
};

export const templeNavItemSx = (active: boolean, indent: number): SxProps<Theme> => ({
  position: 'relative',
  borderRadius: '0 10px 10px 0',
  minHeight: 46,
  pl: indent,
  pr: 1.5,
  gap: 1.5,
  color: active ? TEMPLE.maroon : TEMPLE.inkM,
  transition: 'background-color .2s ease, color .2s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: '0 3px 3px 0',
    backgroundColor: active ? TEMPLE.saffron : 'transparent',
  },
  '& .MuiListItemIcon-root': { color: active ? TEMPLE.saffron : TEMPLE.inkL },
  ...(active && { backgroundColor: TEMPLE.cream3 }),
  '&:hover': {
    backgroundColor: active ? TEMPLE.cream3 : TEMPLE.cream2,
    color: active ? TEMPLE.maroon : TEMPLE.saffronD,
    '& .MuiListItemIcon-root': { color: TEMPLE.saffron },
  },
});


export const TEMPLE_RADIUS = 16;
export const TEMPLE_SHADOW = '0 2px 14px rgba(138, 43, 16, 0.06)';
export const TEMPLE_SHADOW_HOVER = '0 8px 28px rgba(138, 43, 16, 0.12)';

export const templeCardSx: CSSObject = {
  backgroundColor: '#fff',
  border: `1px solid ${TEMPLE.line}`,
  borderRadius: `${TEMPLE_RADIUS}px`,
  boxShadow: TEMPLE_SHADOW,
};

export const templeCardTitleSx: CSSObject = {
  fontFamily: TEMPLE_FONTS.display,
  fontWeight: 700,
  fontSize: '1.15rem',
  lineHeight: 1.3,
  color: TEMPLE.maroon,
};

export const templeOverlineSx: CSSObject = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '1.6px',
  textTransform: 'uppercase',
  color: TEMPLE.inkL,
};

export const templeButtonSx: SxProps<Theme> = {
  borderRadius: '999px',
  px: 2.25,
  py: 0.75,
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
  boxShadow: '0 6px 22px rgba(232, 114, 28, 0.35)',
  '&:hover': {
    background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
    boxShadow: '0 8px 26px rgba(232, 114, 28, 0.45)',
    transform: 'translateY(-1px)',
  },
};

export const templeTableContainerSx: CSSObject = {
  ...templeCardSx,
  overflow: 'auto',
};

export const templeTableNameCellSx: SxProps<Theme> = {
  fontWeight: 600,
  color: TEMPLE.maroon,
};


export type TempleStatusTone = 'success' | 'progress' | 'done' | 'pending' | 'error' | 'default';

export const TEMPLE_STATUS: Record<TempleStatusTone, { bg: string; color: string; border: string }> = {
  success: { bg: '#E7F4EA', color: '#2E7D32', border: '#C3E2C9' },
  progress: { bg: '#FDF1DA', color: '#A87E28', border: '#EBD6A2' },
  done: { bg: '#E4EEF5', color: '#3A6B8A', border: '#C2D8E5' },
  pending: { bg: '#FDF1DA', color: '#A87E28', border: '#EBD6A2' },
  error: { bg: '#FBE7E4', color: '#C1352A', border: '#EFC5BF' },
  default: { bg: TEMPLE.cream3, color: TEMPLE.inkM, border: TEMPLE.line },
};

export const templeStatusTone = (status?: string | null): TempleStatusTone => {
  const s = (status ?? '').toLowerCase().trim();
  if (!s) return 'default';
  if (/(reject|cancel|fail|block|expire|overdue|inactive)/.test(s)) return 'error';
  if (/(complete|done|closed|utilis|utiliz|disburs)/.test(s)) return 'done';
  if (/(progress|ongoing|running|active)/.test(s)) return 'progress';
  if (/(pending|await|review|submit|draft|hold)/.test(s)) return 'pending';
  if (/(approve|accept|open|success|verified|commit|publish)/.test(s)) return 'success';
  return 'default';
};

export const templeStatusChipSx = (tone: TempleStatusTone): CSSObject => {
  const t = TEMPLE_STATUS[tone];
  return {
    height: 24,
    borderRadius: '999px',
    backgroundColor: t.bg,
    color: t.color,
    border: `1px solid ${t.border}`,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    '& .MuiChip-label': { padding: '0 10px' },
  };
};

export const templePaginationSx: CSSObject = {
  '& .MuiPaginationItem-root': {
    borderRadius: '8px',
    fontSize: 13,
    fontWeight: 600,
    color: TEMPLE.inkM,
    borderColor: TEMPLE.line,
    '&:hover': { backgroundColor: TEMPLE.cream2 },
  },
  '& .MuiPaginationItem-root.Mui-selected': {
    background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
    borderColor: 'transparent',
    color: '#fff',
    '&:hover': {
      background: `linear-gradient(135deg, ${TEMPLE.saffron}, ${TEMPLE.saffronD})`,
    },
  },
  '& .MuiPaginationItem-ellipsis': { color: TEMPLE.inkL },
};
